import { httpClient } from './http-client';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config/api';
import type { UserRole } from '../store/auth.store';

export interface UserInfo {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  userInfo: UserInfo;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

class AuthService {
  private readonly endpoints = API_ENDPOINTS.AUTH;

  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return httpClient.post<LoginResponse>(this.endpoints.LOGIN, { email, password });
  }

  async sendVerificationCode(email: string): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.SEND_VERIFICATION_CODE, { email });
  }

  async verifyCodeAndRegister(data: {
    email: string;
    verificationCode: string;
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.VERIFY_CODE_AND_REGISTER, data);
  }

  async forgotPassword(email: string): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.FORGOT_PASSWORD, { email });
  }

  async resetPassword(data: {
    email: string;
    verificationCode: string;
    newPassword: string;
  }): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.RESET_PASSWORD, data);
  }

  saveTokens(token: string, refreshToken: string, userInfo: UserInfo): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
  }

  getStoredToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getStoredRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getStoredUserInfo(): UserInfo | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.USER_INFO);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  }
}

export const authService = new AuthService();
